import React, {useState, useCallback, useContext} from 'react'
import {useDisclosure, Box} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import OrderCancellationDetails from './order-cancellation-details'
import OrderInfo from './order-info'
import {useParams} from 'react-router-dom'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {MRT_API_BASEPATH} from '../../../../ssr/constants'
import CancellationConfirmationModal from './cancellation-confrimation-modal'
import {OrderContext} from '../order-context'
import OrderReturnHeader from './order-return-header'
import LoadingSpinner from '../../../../components/loading-spinner'
import {useTokenHelper} from '../../../../utils/token-helper'
import {isOrderExported} from '../utils'

const OrderSummarySection = React.memo(({order, refetchOrder}) => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [showCancelForm, setShowCancelForm] = useState(false)
    const {activeReturnShipmentId} = useContext(OrderContext)
    const [isLoading, setIsLoading] = useState(false)
    const appOrigin = useOrigin(false)
    const {fetchOMSValidToken} = useTokenHelper()

    const params = useParams()

    const {orderId} = params

    const handleSubmitCancellation = useCallback(
        async (cancellationReason, additionalCancellationReason) => {
            const updatedOrder = await refetchOrder()
            if (!updatedOrder || !updatedOrder.status || isOrderExported(updatedOrder.status)) {
                return
            }
            try {
                setIsLoading(true)
                const accessToken = await fetchOMSValidToken()

                const cancelOrderPayload = {
                    allOrNone: true,
                    compositeRequest: [
                        {
                            method: 'PATCH',
                            url: `/services/data/v59.0/sobjects/OrderSummary/${orderId}`,
                            referenceId: 'refOrder',
                            body: {
                                IsCancelationFromCommerce__c: true,
                                CancelationReason__c: cancellationReason,
                                AdditionalCancelationReason__c: additionalCancellationReason,
                                CancelationDate__c: new Date(),
                            },
                        },
                    ],
                }

                const response = await fetch(`${appOrigin}${MRT_API_BASEPATH}/cancel-order-oms`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(cancelOrderPayload),
                })

                const result = await response.json()

                if (result.success) {
                    onOpen()
                    setShowCancelForm(false)
                }
            } catch (err) {
                // TODO
                console.error('Cancellation failed: ', err)
            } finally {
                setIsLoading(false)
            }
        },
        [orderId, refetchOrder],
    )
    return (
        <Box position="relative">
            {isLoading && <LoadingSpinner />}
            {activeReturnShipmentId ? (
                <OrderReturnHeader
                    orderNumber={order?.orderNumber}
                    orderDate={order?.orderedDate}
                />
            ) : (
                <>
                    {showCancelForm ? (
                        <OrderCancellationDetails
                            order={order}
                            onSubmit={handleSubmitCancellation}
                        />
                    ) : (
                        <OrderInfo order={order} onCancelRequest={() => setShowCancelForm(true)} />
                    )}
                </>
            )}

            <CancellationConfirmationModal
                isOpen={isOpen}
                onClose={() => {
                    refetchOrder()
                    onClose()
                }}
            />
        </Box>
    )
})

OrderSummarySection.propTypes = {
    order: PropTypes.object,
    refetchOrder: PropTypes.func,
}

OrderSummarySection.displayName = 'order-summary-section'

export default OrderSummarySection
